// Schema markup generators for different page types

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Nora AI",
  "url": "https://interview.norahq.com",
  "logo": "https://interview.norahq.com/images/logo/nora-ai-logo.png",
  "description": "AI mock interviewer helping job seekers practice technical, behavioral, and salary negotiation interviews",
  "sameAs": [
    "https://www.linkedin.com/company/norahq",
    "https://www.instagram.com/trynorahq/",
    "https://www.tiktok.com/@norahq"
  ]
});

export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Nora AI",
  "url": "https://interview.norahq.com",
  "description": "AI-powered mock interview platform for job interview preparation",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://interview.norahq.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

export const generateWebPageSchema = (title, description, url) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": title,
  "description": description,
  "url": url,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Nora AI",
    "url": "https://interview.norahq.com"
  }
});

export const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Nora AI Mock Interviewer",
  "description": "AI mock interview platform with technical, behavioral, and salary negotiation practice. Get personalized feedback and land your dream job.",
  "provider": {
    "@type": "Organization",
    "name": "Nora AI"
  },
  "serviceType": "Interview Preparation",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock",
    "price": "0",
    "priceCurrency": "USD"
  }
});

export const generateSoftwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Nora AI",
  "description": "AI-powered interview preparation platform with mock interviews and feedback",
  "url": "https://interview.norahq.com",
  "applicationCategory": "Educational Software",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": "150"
  }
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": crumb.url
  }))
});

export const generatePersonSchema = (person) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": person.name,
  "jobTitle": person.jobTitle,
  "worksFor": {
    "@type": "Organization",
    "name": "Nora AI"
  },
  "image": person.image,
  "description": person.description
});

// Helper function to combine multiple schemas
export const combineSchemas = (schemas = []) => {
  const stripContext = (schemaNode) => {
    if (!schemaNode || typeof schemaNode !== 'object' || Array.isArray(schemaNode)) {
      return schemaNode;
    }

    const { ["@context"]: _ignoredContext, ...rest } = schemaNode;
    return rest;
  };

  const normalizedSchemas = [];

  schemas.forEach((schema) => {
    if (!schema) {
      return;
    }

    if (Array.isArray(schema)) {
      schema.forEach((nested) => {
        if (nested) {
          normalizedSchemas.push(stripContext(nested));
        }
      });
      return;
    }

    if (schema["@graph"] && Array.isArray(schema["@graph"])) {
      schema["@graph"].forEach((nested) => {
        if (nested) {
          normalizedSchemas.push(stripContext(nested));
        }
      });
      return;
    }

    normalizedSchemas.push(stripContext(schema));
  });

  return {
    "@context": "https://schema.org",
    "@graph": normalizedSchemas
  };
};

// Helper function to generate JSON-LD script tag
export const generateSchemaScript = (schema) => {
  return `<script type="application/ld+json">
${JSON.stringify(schema, null, 2)}
</script>`;
};
